import { useState } from "react";
import { Mail, Instagram, Send, MessageSquare } from "lucide-react";
import Reveal from "@/components/Reveal";
import { EMAIL, INSTAGRAM_URL, INSTAGRAM_HANDLE, SMS_URL, PHONE_DISPLAY } from "@/config";

export default function Contact() {
  const [name, setName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("Premier contact");
  const [message, setMessage] = useState("");

  const buildMailto = () => {
    const body = [
      message,
      "",
      "—",
      `De : ${name || "(non renseigné)"}`,
      replyTo ? `Répondre à : ${replyTo}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    return `mailto:${EMAIL}?subject=${encodeURIComponent(
      `[Site web] ${subject} — ${name || ""}`
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = buildMailto();
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <Reveal>
          <p className="font-display text-xs tracking-[0.2em] uppercase text-taupe">Contact</p>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight tracking-tight text-charcoal sm:text-5xl">
            Parlons de ce qui se joue pour vous
          </h1>
          <p className="mt-8 max-w-prose text-base leading-relaxed text-stone-700">
            Que vous soyez un jeune ou un parent, écrivez-moi. Ce formulaire
            ouvre votre messagerie avec un message pré-rempli — rien n'est envoyé sans vous.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-14 md:grid-cols-[3fr_2fr]">
          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-8" data-testid="contact-form">
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">
                    Votre nom
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-3 w-full border-b border-stone-300 bg-transparent py-3 text-base text-charcoal outline-none transition-colors focus:border-taupe"
                    placeholder="Prénom Nom"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">
                    Votre e-mail
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    className="mt-3 w-full border-b border-stone-300 bg-transparent py-3 text-base text-charcoal outline-none transition-colors focus:border-taupe"
                    placeholder="vous@exemple.fr"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">
                  Sujet
                </label>
                <select
                  id="contact-subject"
                  data-testid="contact-subject-select"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-3 w-full border-b border-stone-300 bg-transparent py-3 text-base text-charcoal outline-none transition-colors focus:border-taupe"
                >
                  <option>Premier contact</option>
                  <option>Question sur un accompagnement</option>
                  <option>Demande de rendez-vous</option>
                  <option>Autre demande</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">
                  Votre message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-textarea"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-3 w-full resize-y border-b border-stone-300 bg-transparent py-3 text-base leading-relaxed text-charcoal outline-none transition-colors focus:border-taupe"
                  placeholder="Racontez-moi en quelques mots ce qui vous amène…"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-button"
                className="inline-flex w-fit items-center gap-2 rounded-full bg-taupe px-8 py-3.5 font-display text-sm text-bone transition-colors hover:bg-taupe-dark"
              >
                Ouvrir ma messagerie <Send size={16} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.2}>
            <aside className="flex flex-col gap-8">
              <div className="rounded-2xl border border-stone-200 bg-sand p-8" data-testid="contact-agenda-block">
                <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">Rendez-vous</p>
                <p className="mt-4 text-sm leading-relaxed text-stone-500">
                  Pour prendre rendez-vous, envoyez-moi simplement un SMS au {PHONE_DISPLAY} — je
                  vous réponds personnellement.
                </p>
                <a
                  href={SMS_URL}
                  data-testid="contact-agenda-button"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-taupe px-6 py-3 font-display text-sm text-bone transition-colors hover:bg-taupe-dark"
                >
                  <MessageSquare size={16} /> Prendre rendez-vous par SMS
                </a>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-sand p-8" data-testid="contact-direct-block">
                <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">En direct</p>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-mailto-link"
                  className="mt-4 inline-flex items-center gap-2 text-base text-charcoal transition-colors hover:text-taupe"
                >
                  <Mail size={18} className="text-taupe" /> {EMAIL}
                </a>
                <p className="mt-4 text-sm leading-relaxed text-stone-500">
                  Je réponds personnellement à chaque message.
                </p>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-sand p-8" data-testid="contact-cabinet-block">
                <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">Le cabinet principal</p>
                <img
                  src="/cabinet.jpg"
                  alt="Le cabinet principal : fauteuils accueillants et espace lumineux"
                  data-testid="contact-cabinet-image"
                  className="mt-4 aspect-[4/3] w-full rounded-xl object-cover"
                />
                <p className="mt-4 text-sm leading-relaxed text-stone-500">
                  Un espace chaleureux et coloré, pensé pour que les jeunes s'y sentent bien dès
                  la première séance.
                </p>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-sand p-8" data-testid="contact-instagram-block">
                <p className="font-display text-xs tracking-[0.2em] uppercase text-stone-500">Instagram</p>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="mt-4 inline-flex items-center gap-2 text-base text-charcoal transition-colors hover:text-taupe"
                >
                  <Instagram size={18} className="text-taupe" /> {INSTAGRAM_HANDLE}
                </a>
                <p className="mt-4 text-sm leading-relaxed text-stone-500">
                  Culture de la neurodiversité, ressources et coulisses du cabinet.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
